import { useMutation } from "@apollo/client/react";
import { useCallback, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import {
  GetRelationTypeDetailDocument,
  GetRelationTypesDocument,
  UpsertRelationTypeDocument,
  type UpsertRelationTypeMutation,
  type UpsertRelationTypeMutationVariables,
} from "~/features/admin/relationTypes/gqls";
import type { RelationTypeEditFormData } from "~/features/admin/relationTypes/types";
import { useTenant } from "~/features/tenant/context";
import { translateError } from "~/lib/errors";

interface UseRelationTypeEditOptions {
  onSuccess?: () => void;
}

/**
 * Edit a relation type via `cnutUpsertRelationType` (V1 parity). Refetches the
 * detail and the tenant-scoped list so both views reflect the change.
 */
export function useRelationTypeEdit({ onSuccess }: UseRelationTypeEditOptions = {}) {
  const { tenantId } = useTenant();
  if (!tenantId) {
    throw new Error("Tenant ID is required");
  }
  const { t } = useTranslation();
  const [processing, setProcessing] = useState(false);
  const [upsertRelationType] = useMutation<UpsertRelationTypeMutation, UpsertRelationTypeMutationVariables>(
    UpsertRelationTypeDocument,
  );

  const submit = useCallback(
    async (formData: RelationTypeEditFormData) => {
      setProcessing(true);
      try {
        await upsertRelationType({
          refetchQueries: [
            { query: GetRelationTypeDetailDocument, variables: { tenantId, relationTypeId: formData.id } },
            { query: GetRelationTypesDocument, variables: { tenantId } },
          ],
          variables: {
            input: {
              tenantId,
              id: formData.id,
              name: formData.name,
              sourceCategoryId: formData.sourceCategoryId,
              targetCategoryId: formData.targetCategoryId,
            },
          },
        });
        toast.success(t("feature.common.updatedSuccessfully", { model: "relationType" }));
        onSuccess?.();
      } catch (e) {
        toast.error(translateError(t, e));
      } finally {
        setProcessing(false);
      }
    },
    [upsertRelationType, tenantId, t, onSuccess],
  );

  return { submit, processing };
}
